export type NovaMenuGroup = "food" | "drinks";

export type NovaMenuVariant = {
  id: string;
  label: string;
  price: number;
};

export type NovaMenuCategory = {
  id: string;
  label: string;
  group: NovaMenuGroup;
  sortOrder: number;
};

export type NovaMenuItem = {
  id: string;
  categoryId: string;
  name: string;
  description?: string;
  price?: number;
  variants?: readonly NovaMenuVariant[];
  image?: string;
  available: boolean;
  featured: boolean;
  orderingEnabled: boolean;
  sortOrder: number;
};

export type NovaMenuModifier = {
  id: string;
  name: string;
  priceDelta: number;
  categoryIds: readonly string[];
};

/**
 * NOVA RES CULINA — CANONICAL MENU REGISTRY
 *
 * Source:
 * Client-supplied Nova food and drinks menus.
 *
 * Rules:
 * - Do not invent missing descriptions.
 * - Do not invent food photography.
 * - Prices are stored as numbers in KES.
 * - CMS will eventually become the source of truth.
 * - This file is the structured migration foundation.
 */

export const NOVA_MENU_CATEGORIES: readonly NovaMenuCategory[] = [
  {
    id: "breakfast",
    label: "Breakfast",
    group: "food",
    sortOrder: 10,
  },
  {
    id: "combos",
    label: "Combos",
    group: "food",
    sortOrder: 20,
  },
  {
    id: "quick-bites",
    label: "Quick Bites",
    group: "food",
    sortOrder: 30,
  },
  {
    id: "potato-fiesta",
    label: "Potato Fiesta",
    group: "food",
    sortOrder: 40,
  },
  {
    id: "sandwiches",
    label: "Sandwiches",
    group: "food",
    sortOrder: 50,
  },
  {
    id: "authentically-swahili",
    label: "Authentically Swahili",
    group: "food",
    sortOrder: 60,
  },
  {
    id: "chicken",
    label: "Wing It With Some Chicken",
    group: "food",
    sortOrder: 70,
  },
  {
    id: "from-the-sea",
    label: "From The Sea",
    group: "food",
    sortOrder: 80,
  },
  {
    id: "desserts",
    label: "Desserts",
    group: "food",
    sortOrder: 90,
  },

  {
    id: "coffees",
    label: "Coffees",
    group: "drinks",
    sortOrder: 100,
  },
  {
    id: "teas",
    label: "Teas",
    group: "drinks",
    sortOrder: 110,
  },
  {
    id: "shakes",
    label: "Shakes",
    group: "drinks",
    sortOrder: 120,
  },
  {
    id: "mocktails",
    label: "Mocktails",
    group: "drinks",
    sortOrder: 130,
  },
  {
    id: "go-bananas-smoothies",
    label: "Go Bananas Smoothies",
    group: "drinks",
    sortOrder: 140,
  },
  {
    id: "healthy-me-smoothies",
    label: "Healthy Me Smoothies",
    group: "drinks",
    sortOrder: 150,
  },
] as const;

export const NOVA_MENU_ITEMS: readonly NovaMenuItem[] = [
  // =====================================================
  // BREAKFAST
  // =====================================================

  {
    id: "pancake-stack",
    categoryId: "breakfast",
    name: "Pancake Stack",
    description:
      "Soft golden pancakes stacked high and served with syrup.",
    price: 300,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 10,
  },
  {
    id: "bacon-waffles",
    categoryId: "breakfast",
    name: "Bacon Waffles",
    description:
      "Crispy golden waffles made with bacon-loaded batter and finished with maple syrup.",
    price: 300,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 20,
  },
  {
    id: "plain-toast",
    categoryId: "breakfast",
    name: "Plain Toast",
    description: "Freshly toasted slices of bread.",
    price: 150,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 30,
  },
  {
    id: "french-toast",
    categoryId: "breakfast",
    name: "French Toast",
    description:
      "Thick slices of bread dipped in cinnamon egg batter and toasted until golden brown.",
    price: 200,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 40,
  },
  {
    id: "rolled-omelette",
    categoryId: "breakfast",
    name: "Rolled Omelette",
    description:
      "Light fluffy omelette rolled with savory fillings and cooked to perfection.",
    price: 200,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 50,
  },
  {
    id: "eggs-on-toast",
    categoryId: "breakfast",
    name: "Eggs On Toast",
    description:
      "Freshly cooked eggs served on toasted bread with a simple, hearty finish.",
    price: 200,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 60,
  },

  // =====================================================
  // COMBOS
  // =====================================================

  {
    id: "waffle-burger",
    categoryId: "combos",
    name: "Waffle Burger",
    description:
      "Juicy burger layered between crispy golden waffles for the perfect sweet-and-savory bite. Chicken or bacon, cheese, lettuce, fried egg and pickled onions.",
    price: 750,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 10,
  },
  {
    id: "nova-protein-burst",
    categoryId: "combos",
    name: "Nova Protein Burst",
    description:
      "A hearty high-protein breakfast platter with beef samosas, sausages or bacon, fried eggs, breakfast potatoes, and your choice of juice or coffee.",
    price: 870,
    available: true,
    featured: true,
    orderingEnabled: true,
    sortOrder: 20,
  },

  // =====================================================
  // QUICK BITES
  // =====================================================

  {
    id: "chapati",
    categoryId: "quick-bites",
    name: "Chapati",
    price: 100,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 10,
  },
  {
    id: "moroccan-chapati",
    categoryId: "quick-bites",
    name: "Moroccan Chapati",
    price: 150,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 20,
  },
  {
    id: "muffins",
    categoryId: "quick-bites",
    name: "Muffins",
    price: 200,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 30,
  },
  {
    id: "samosa-2pc",
    categoryId: "quick-bites",
    name: "Samosa (2pc)",
    price: 150,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 40,
  },
  {
    id: "sausage-2pc",
    categoryId: "quick-bites",
    name: "Sausage (2pc)",
    price: 150,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 50,
  },

  // =====================================================
  // POTATO FIESTA
  // =====================================================

  {
    id: "plain-fries-or-potato-wedges",
    categoryId: "potato-fiesta",
    name: "Plain Fries or Potato Wedges",
    description:
      "Crispy golden fries lightly seasoned and served hot; thick-cut potato wedges seasoned and crisped to a golden finish.",
    price: 350,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 10,
  },
  {
    id: "viazi-karai",
    categoryId: "potato-fiesta",
    name: "Viazi Karai",
    description:
      "Turmeric-coated potatoes, dipped in batter, deep fried and served with tangy chutney.",
    price: 350,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 20,
  },
  {
    id: "fajita-chicken-loaded-potatoes",
    categoryId: "potato-fiesta",
    name: "Fajita Chicken Loaded Potatoes",
    description:
      "Crispy potatoes loaded with spicy fajita chicken, sautéed peppers, melted cheese, and bold smoky flavors.",
    price: 850,
    available: true,
    featured: true,
    orderingEnabled: true,
    sortOrder: 30,
  },

  // =====================================================
  // SANDWICHES
  // =====================================================

  {
    id: "cheese-and-ham",
    categoryId: "sandwiches",
    name: "Cheese and Ham",
    description:
      "Freshly baked bread, sliced cheese, ham and tomatoes served with a dash of fries and salsa.",
    price: 600,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 10,
  },
  {
    id: "egg-mayo",
    categoryId: "sandwiches",
    name: "Egg Mayo",
    description:
      "Freshly baked bread, chopped boiled eggs and garlic mayo served with a dash of fries and salsa.",
    price: 500,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 20,
  },
  {
    id: "cheesy-chicken",
    categoryId: "sandwiches",
    name: "Cheesy Chicken",
    description:
      "Freshly baked bread, chicken breast, sliced cheese, tomatoes and onion served with a dash of fries and salsa.",
    price: 600,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 30,
  },

  // =====================================================
  // AUTHENTICALLY SWAHILI
  // =====================================================

  {
    id: "bhajia",
    categoryId: "authentically-swahili",
    name: "Bhajia",
    description:
      "Crispy golden fritters made from spiced gram flour or blended legumes, deep-fried to perfection.",
    price: 200,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 10,
  },
  {
    id: "maharagwe-ya-nazi",
    categoryId: "authentically-swahili",
    name: "Maharagwe ya Nazi",
    description:
      "Slow-cooked beans simmered in rich coconut sauce with coastal spices.",
    price: 400,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 20,
  },
  {
    id: "beef-pilau",
    categoryId: "authentically-swahili",
    name: "Beef Pilau",
    description:
      "Fragrant spiced rice cooked with tender beef, infused with traditional pilau spices.",
    price: 600,
    available: true,
    featured: true,
    orderingEnabled: true,
    sortOrder: 30,
  },

  // =====================================================
  // CHICKEN
  // =====================================================

  {
    id: "wings",
    categoryId: "chicken",
    name: "Wings",
    description:
      "Juicy wings glazed in BBQ sauce and grilled to sticky, flavorful perfection.",
    price: 900,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 10,
  },
  {
    id: "drummer-boy",
    categoryId: "chicken",
    name: "Drummer Boy",
    description:
      "Tender chicken drumsticks coated in rich BBQ sauce and slow-grilled.",
    price: 1100,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 20,
  },
  {
    id: "chicken-curry",
    categoryId: "chicken",
    name: "Chicken Curry",
    description:
      "Tender chicken simmered in a spiced, flavorful curry sauce.",
    price: 900,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 30,
  },
  {
    id: "stir-fry",
    categoryId: "chicken",
    name: "Stir Fry",
    description:
      "Sautéed chicken tossed with fresh vegetables in a light, savory stir-fry sauce.",
    price: 900,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 40,
  },
  {
    id: "kienyeji-chicken",
    categoryId: "chicken",
    name: "Kienyeji Chicken",
    price: 1300,
    available: true,
    featured: true,
    orderingEnabled: true,
    sortOrder: 50,
  },

  // =====================================================
  // FROM THE SEA
  // =====================================================

  {
    id: "fish-fingers",
    categoryId: "from-the-sea",
    name: "Fish Fingers",
    description:
      "Golden, crispy strips of tender fish fillet coated in seasoned breadcrumbs, served with your choice of accompaniment.",
    price: 1200,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 10,
  },
  {
    id: "whole-tilapia-fish",
    categoryId: "from-the-sea",
    name: "Whole Tilapia Fish",
    description:
      "Fresh whole tilapia, expertly seasoned and cooked to your preference, served with your choice of accompaniment.",
    price: 1100,
    available: true,
    featured: true,
    orderingEnabled: true,
    sortOrder: 20,
  },
  {
    id: "fish-fillet",
    categoryId: "from-the-sea",
    name: "Fish Fillet",
    description:
      "A succulent boneless fish fillet, delicately seasoned and perfectly cooked, served with your choice of accompaniment.",
    price: 1200,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 30,
  },

  // =====================================================
  // DESSERTS
  // =====================================================

  {
    id: "ice-cream-two-scoops",
    categoryId: "desserts",
    name: "Ice Cream (2 Scoops)",
    price: 300,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 10,
  },
  {
    id: "cake-slice-of-the-day",
    categoryId: "desserts",
    name: "Cake Slice of the Day",
    price: 300,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 20,
  },

  // =====================================================
  // COFFEES
  // =====================================================

  {
    id: "espresso",
    categoryId: "coffees",
    name: "Espresso",
    variants: [
      { id: "single", label: "Single", price: 180 },
      { id: "double", label: "Double", price: 250 },
    ],
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 10,
  },
  {
    id: "americano",
    categoryId: "coffees",
    name: "Americano",
    price: 180,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 20,
  },
  {
    id: "latte",
    categoryId: "coffees",
    name: "Latte",
    variants: [
      { id: "single", label: "Single", price: 290 },
      { id: "double", label: "Double", price: 330 },
    ],
    available: true,
    featured: true,
    orderingEnabled: true,
    sortOrder: 30,
  },
  {
    id: "mocha",
    categoryId: "coffees",
    name: "Mocha",
    variants: [
      { id: "single", label: "Single", price: 310 },
      { id: "double", label: "Double", price: 360 },
    ],
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 40,
  },
  {
    id: "cappuccino",
    categoryId: "coffees",
    name: "Cappuccino",
    variants: [
      { id: "single", label: "Single", price: 290 },
      { id: "double", label: "Double", price: 330 },
    ],
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 50,
  },
  {
    id: "house-coffee",
    categoryId: "coffees",
    name: "House Coffee",
    price: 200,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 60,
  },

  // =====================================================
  // TEAS
  // =====================================================

  {
    id: "mixed-tea",
    categoryId: "teas",
    name: "Mixed Tea",
    price: 200,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 10,
  },
  {
    id: "ginger-tea",
    categoryId: "teas",
    name: "Ginger Tea",
    price: 230,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 20,
  },
  {
    id: "nova-chai-masala",
    categoryId: "teas",
    name: "Nova Chai (Masala)",
    price: 200,
    available: true,
    featured: true,
    orderingEnabled: true,
    sortOrder: 30,
  },
  {
    id: "black-tea",
    categoryId: "teas",
    name: "Black Tea",
    price: 150,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 40,
  },
  {
    id: "hibiscus-tea",
    categoryId: "teas",
    name: "Hibiscus Tea",
    price: 250,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 50,
  },
  {
    id: "dawa",
    categoryId: "teas",
    name: "Dawa",
    price: 250,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 60,
  },

  // =====================================================
  // SHAKES
  // =====================================================

  {
    id: "classic-vanilla-shake",
    categoryId: "shakes",
    name: "Classic Vanilla",
    price: 600,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 10,
  },
  {
    id: "chocolate-shake",
    categoryId: "shakes",
    name: "Chocolate",
    price: 600,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 20,
  },
  {
    id: "salted-caramel-shake",
    categoryId: "shakes",
    name: "Salted Caramel",
    price: 650,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 30,
  },
  {
    id: "pink-blush",
    categoryId: "shakes",
    name: "Pink Blush",
    price: 650,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 40,
  },
  {
    id: "purple-bloom",
    categoryId: "shakes",
    name: "Purple Bloom",
    price: 650,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 50,
  },
  {
    id: "coffee-shake",
    categoryId: "shakes",
    name: "Coffee",
    price: 650,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 60,
  },
  {
    id: "oreo-shake",
    categoryId: "shakes",
    name: "Oreo",
    price: 650,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 70,
  },
  {
    id: "nova-berry-royale",
    categoryId: "shakes",
    name: "Nova Berry Royale",
    price: 650,
    available: true,
    featured: true,
    orderingEnabled: true,
    sortOrder: 80,
  },

  // =====================================================
  // MOCKTAILS
  // =====================================================

  {
    id: "mojito",
    categoryId: "mocktails",
    name: "Mojito",
    price: 450,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 10,
  },
  {
    id: "lemonade",
    categoryId: "mocktails",
    name: "Lemonade",
    price: 450,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 20,
  },
  {
    id: "blue-lagoon",
    categoryId: "mocktails",
    name: "Blue Lagoon",
    price: 450,
    available: true,
    featured: true,
    orderingEnabled: true,
    sortOrder: 30,
  },
  {
    id: "fresh-juice",
    categoryId: "mocktails",
    name: "Fresh Juice",
    price: 350,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 40,
  },

  // =====================================================
  // GO BANANAS SMOOTHIES
  // =====================================================

  {
    id: "chocolate-peanut-butter-smoothie",
    categoryId: "go-bananas-smoothies",
    name: "Chocolate Peanut Butter",
    price: 650,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 10,
  },
  {
    id: "creamy-strawberry-banana",
    categoryId: "go-bananas-smoothies",
    name: "Creamy Strawberry Banana",
    price: 650,
    available: true,
    featured: true,
    orderingEnabled: true,
    sortOrder: 20,
  },
  {
    id: "peanut-butter-banana",
    categoryId: "go-bananas-smoothies",
    name: "Peanut Butter Banana",
    price: 650,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 30,
  },

  // =====================================================
  // HEALTHY ME SMOOTHIES
  // =====================================================

  {
    id: "uji-power",
    categoryId: "healthy-me-smoothies",
    name: "Uji Power",
    price: 300,
    available: true,
    featured: false,
    orderingEnabled: true,
    sortOrder: 10,
  },
  {
    id: "green-monster",
    categoryId: "healthy-me-smoothies",
    name: "Green Monster",
    price: 690,
    available: true,
    featured: true,
    orderingEnabled: true,
    sortOrder: 20,
  },
] as const;

export const NOVA_MENU_MODIFIERS: readonly NovaMenuModifier[] = [
  {
    id: "additional-syrup",
    name: "Additional Syrup",
    priceDelta: 100,
    categoryIds: ["coffees"],
  },
] as const;

export const getNovaMenuCategory = (categoryId: string) =>
  NOVA_MENU_CATEGORIES.find(
    (category) => category.id === categoryId,
  );

export const getNovaMenuItemsForCategory = (
  categoryId: string,
) =>
  NOVA_MENU_ITEMS.filter(
    (item) => item.categoryId === categoryId,
  ).sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

export const getNovaMenuStartingPrice = (
  item: NovaMenuItem,
) => {
  if (typeof item.price === "number") {
    return item.price;
  }

  if (item.variants?.length) {
    return Math.min(
      ...item.variants.map((variant) => variant.price),
    );
  }

  return 0;
};
